import { useCallback, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "pixley_bubble_pop_muted";

const SOUND_CONFIG = {
  pop: { frequencies: [720, 980], duration: 0.12, type: "sine", gain: 0.06 },
  wrong: { frequencies: [280], duration: 0.14, type: "sawtooth", gain: 0.05 },
  combo: { frequencies: [660, 880, 1180], duration: 0.16, type: "triangle", gain: 0.06 },
  miss: { frequencies: [220], duration: 0.1, type: "sine", gain: 0.04 },
  victory: { frequencies: [660, 880, 1120, 1400], duration: 0.18, type: "triangle", gain: 0.065 },
  gameover: { frequencies: [420, 300, 220], duration: 0.18, type: "sine", gain: 0.05 },
  click: { frequencies: [520], duration: 0.05, type: "square", gain: 0.04 },
  hover: { frequencies: [770], duration: 0.03, type: "sine", gain: 0.02 },
};

const createAudioContext = () => {
  if (typeof window === "undefined") return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  return Ctx ? new Ctx() : null;
};

export const useBubbleSoundManager = () => {
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const audioContextRef = useRef(null);
  const warmRef = useRef(false);

  const ensureReady = useCallback(async () => {
    if (!audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
    const ctx = audioContextRef.current;
    if (!ctx) return null;
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    if (!warmRef.current) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.01);
      warmRef.current = true;
    }
    return ctx;
  }, []);

  const play = useCallback(
    async (kind) => {
      if (muted) return;
      const config = SOUND_CONFIG[kind];
      if (!config) return;
      const ctx = await ensureReady();
      if (!ctx) return;

      config.frequencies.forEach((frequency, index) => {
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();
        osc.type = config.type;
        osc.frequency.value = frequency;
        gainNode.gain.setValueAtTime(config.gain, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + config.duration);
        osc.connect(gainNode);
        gainNode.connect(ctx.destination);
        const startAt = ctx.currentTime + index * 0.03;
        osc.start(startAt);
        osc.stop(startAt + config.duration);
      });
    },
    [muted, ensureReady]
  );

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Ignore storage issues.
      }
      return next;
    });
  }, []);

  return useMemo(
    () => ({
      muted,
      toggleMute,
      play,
      prewarm: ensureReady,
    }),
    [muted, toggleMute, play, ensureReady]
  );
};
